import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertCircle, Loader2, RefreshCw } from "lucide-react";

function levelBadge(level: string) {
  if (level === "error") return <Badge variant="destructive" className="text-[10px]">error</Badge>;
  if (level === "warn") return <Badge variant="secondary" className="text-[10px] bg-amber-500/15 text-amber-700 border-amber-500/30">warn</Badge>;
  return <Badge variant="outline" className="text-[10px]">{level}</Badge>;
}

function fmtTime(d: any) {
  if (!d) return "—";
  const date = new Date(d);
  const today = new Date();
  if (date.toDateString() === today.toDateString()) {
    return date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
  }
  return date.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function ErrorsView() {
  // Abre filtrado em "error": warn/info ficam visíveis via dropdown, mas
  // o painel começa mostrando só o que é acionável.
  const [level, setLevel] = useState<"all" | "error" | "warn">("error");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const { data, isLoading, refetch, isFetching } = trpc.superAdmin.systemLogs.useQuery({
    level: level === "all" ? undefined : level,
    context: search || undefined,
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Select value={level} onValueChange={(v: any) => { setLevel(v); setPage(0); }}>
          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os níveis</SelectItem>
            <SelectItem value="error">Erros</SelectItem>
            <SelectItem value="warn">Avisos</SelectItem>
          </SelectContent>
        </Select>
        <Input placeholder="Filtrar por contexto…" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} className="max-w-xs" />
        <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isFetching ? "animate-spin" : ""}`} />
          Atualizar
        </Button>
        <span className="text-xs text-muted-foreground ml-auto">{total} registro(s)</span>
      </div>
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">Nenhum erro ou aviso recente.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[80px]">Nível</TableHead>
                  <TableHead className="w-[180px]">Quando</TableHead>
                  <TableHead>Contexto</TableHead>
                  <TableHead>Mensagem</TableHead>
                  <TableHead className="w-[80px]">Tenant</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r: any) => (
                  <TableRow key={r.id}>
                    <TableCell>{levelBadge(r.level)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtTime(r.createdAt)}</TableCell>
                    <TableCell><code className="text-[11px] bg-muted px-1.5 py-0.5 rounded">{r.context}</code></TableCell>
                    <TableCell className="max-w-[400px]">
                      <div className="text-sm truncate">{r.message}</div>
                      {r.stack && (
                        <details className="mt-1">
                          <summary className="text-[11px] text-muted-foreground cursor-pointer hover:text-foreground">stack</summary>
                          <pre className="text-[10px] bg-muted/50 p-2 mt-1 rounded overflow-auto max-h-[200px] whitespace-pre-wrap">{r.stack}</pre>
                        </details>
                      )}
                    </TableCell>
                    <TableCell className="text-xs">{r.tenantId ?? "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Anterior</Button>
          <span className="text-xs text-muted-foreground">{page + 1} de {totalPages}</span>
          <Button size="sm" variant="outline" disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)}>Próximo</Button>
        </div>
      )}
    </div>
  );
}

function QueueView() {
  const { data, isLoading, refetch, isFetching } = trpc.superAdmin.queueStatus.useQuery();
  if (isLoading) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const counts = data ?? {};
  const queues = Object.keys(counts);
  if (queues.length === 0) {
    return <p className="text-sm text-muted-foreground text-center py-8">Fila pg-boss não está acessível.</p>;
  }
  return (
    <div className="space-y-3">
      <div className="flex justify-end">
        <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${isFetching ? "animate-spin" : ""}`} /> Atualizar
        </Button>
      </div>
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fila</TableHead>
                <TableHead className="text-right">Active</TableHead>
                <TableHead className="text-right">Completed</TableHead>
                <TableHead className="text-right">Failed</TableHead>
                <TableHead className="text-right">Archived</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {queues.map(q => {
                const states = counts[q] as Record<string, number>;
                return (
                  <TableRow key={q}>
                    <TableCell className="font-mono text-xs">{q}</TableCell>
                    <TableCell className="text-right">{states.active ?? 0}</TableCell>
                    <TableCell className="text-right">{states.completed ?? 0}</TableCell>
                    <TableCell className={`text-right ${(states.failed ?? 0) > 0 ? "text-red-600 font-semibold" : ""}`}>{states.failed ?? 0}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{states.archived ?? 0}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function FlowFailsView() {
  const { data, isLoading } = trpc.superAdmin.failedFlowExecutions.useQuery();
  if (isLoading) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const rows = data ?? [];
  if (rows.length === 0) return <p className="text-sm text-muted-foreground text-center py-8">Sem fluxos com falha nas últimas 24h.</p>;
  return (
    <Card><CardContent className="p-0"><Table>
      <TableHeader><TableRow>
        <TableHead className="w-[160px]">Quando</TableHead>
        <TableHead>Tenant</TableHead>
        <TableHead>Fluxo</TableHead>
        <TableHead>Nó</TableHead>
        <TableHead>Erro</TableHead>
      </TableRow></TableHeader>
      <TableBody>
        {rows.map((r: any) => (
          <TableRow key={r.id}>
            <TableCell className="text-xs text-muted-foreground">{fmtTime(r.startedAt)}</TableCell>
            <TableCell className="text-sm">{r.tenantName ?? r.tenantId}</TableCell>
            <TableCell className="text-sm">{r.flowName ?? `#${r.flowId}`}</TableCell>
            <TableCell><code className="text-[11px] bg-muted px-1.5 py-0.5 rounded">{r.currentNodeId ?? "—"}</code></TableCell>
            <TableCell className="text-sm text-red-600 max-w-[400px] truncate">{r.errorMessage ?? "(sem detalhe)"}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table></CardContent></Card>
  );
}

function WebhookFailsView() {
  const { data, isLoading } = trpc.superAdmin.failedWebhooks.useQuery();
  if (isLoading) return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  const rows = data ?? [];
  if (rows.length === 0) return <p className="text-sm text-muted-foreground text-center py-8">Sem webhooks com falha nas últimas 24h.</p>;
  return (
    <Card><CardContent className="p-0"><Table>
      <TableHeader><TableRow>
        <TableHead className="w-[160px]">Quando</TableHead>
        <TableHead>Tipo</TableHead>
        <TableHead>Event ID</TableHead>
        <TableHead>Erro</TableHead>
      </TableRow></TableHeader>
      <TableBody>
        {rows.map((r: any) => (
          <TableRow key={r.id}>
            <TableCell className="text-xs text-muted-foreground">{fmtTime(r.processedAt ?? r.createdAt)}</TableCell>
            <TableCell><Badge variant="outline" className="text-[10px]">{r.eventType}</Badge></TableCell>
            <TableCell><code className="text-[11px]">{r.eventId}</code></TableCell>
            <TableCell className="text-sm text-red-600 max-w-[400px] truncate">{r.errorMessage ?? "(sem detalhe)"}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table></CardContent></Card>
  );
}

export function ObservabilityTab() {
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold tracking-tight flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-amber-500" />
          Observabilidade
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Erros 5xx do sistema, webhooks com falha persistente, status das filas e fluxos quebrados.
        </p>
      </div>
      <Tabs defaultValue="errors">
        <TabsList>
          <TabsTrigger value="errors">Erros do sistema</TabsTrigger>
          <TabsTrigger value="webhooks">Webhooks falhos</TabsTrigger>
          <TabsTrigger value="queue">Filas (pg-boss)</TabsTrigger>
          <TabsTrigger value="flows">Fluxos com falha</TabsTrigger>
        </TabsList>
        <TabsContent value="errors" className="mt-4"><ErrorsView /></TabsContent>
        <TabsContent value="webhooks" className="mt-4"><WebhookFailsView /></TabsContent>
        <TabsContent value="queue" className="mt-4"><QueueView /></TabsContent>
        <TabsContent value="flows" className="mt-4"><FlowFailsView /></TabsContent>
      </Tabs>
    </div>
  );
}
